import { useState } from 'react';
import {
  Modal,
  Typography,
  TextArea,
  IconButton,
  Icon,
  Datepicker,
  FormItem,
} from '@getsynapse/design-system';
import intl from 'react-intl-universal';
import { rangeDate } from 'utils/customTypes';
import completedIcon from 'assets/icons/completed.svg';
import { toCompletionTimestamp } from './helpers';

type CompletedModalProps = {
  isOpen: boolean;
  onCancel: () => void;
  onComplete: ({
    comments,
    completedAt,
  }: {
    comments?: string | undefined;
    completedAt?: string | undefined;
  }) => void;
};

// CGP-180 / CGP-253: confirmation modal for marking an Approved request as
// Completed. Collects an editable Actual Completion Date (pre-filled with
// today, required) and an optional comment. The date is sent to the backend
// as an ISO 8601 string in `data.completed.completedAt`, which CGP-256
// persists (falling back to the server's `now` when absent).
const CompletedModal = ({
  isOpen,
  onCancel,
  onComplete,
}: CompletedModalProps) => {
  const [comments, setComments] = useState<string>();
  const [completionDate, setCompletionDate] = useState<Date | undefined>(
    new Date()
  );
  const [dateError, setDateError] = useState(false);

  const resetForm = () => {
    setComments('');
    setCompletionDate(new Date());
    setDateError(false);
  };

  const handleClose = () => {
    onCancel();
    resetForm();
  };

  const handleDatePick = (date: rangeDate) => {
    const picked = date.startDate ? new Date(date.startDate) : undefined;
    setCompletionDate(picked);
    setDateError(!picked);
  };

  const handleComplete = () => {
    if (!completionDate) {
      setDateError(true);
      return;
    }
    onComplete({
      comments,
      completedAt: toCompletionTimestamp(completionDate),
    });
  };

  // CGP-253: match the Figma modal (600px wide). `size='medium'` is 608px, so
  // the exact width is pinned via inline style — the Modal spreads unknown
  // props onto its content div but overwrites `className`, so `style` is the
  // reliable lever. `childrenClassName` drops the Modal's default `max-h-80`
  // (320px) + `overflow-y-auto` (so the content no longer scrolls) and its
  // `pr-7` — the Modal only adds that right padding to clear its built-in
  // close button, which this modal doesn't use (it renders its own).
  return (
    <Modal
      aria-label='completed-request-modal'
      style={{ width: '600px', minWidth: '600px', maxWidth: '600px' }}
      childrenClassName='overflow-y-visible pr-0'
      actionButtons={[
        {
          children: intl.get(
            'REQUESTS_LIST_PAGE.MODAL.COMPLETE_REQUEST.CONFIRM_BUTTON'
          ),
          variant: 'primary',
          onClick: handleComplete,
          'data-cy': 'request-complete-button',
          className: 'h-10',
        },
        {
          children: intl.get(
            'REQUESTS_LIST_PAGE.MODAL.DELETE_REQUEST.CANCEL_BUTTON'
          ),
          variant: 'tertiary',
          onClick: handleClose,
          'data-cy': 'request-complete-cancel-button',
          className: 'h-10',
        },
      ]}
      data-testid='completed-request-modal'
      closeModal={handleClose}
      isOpen={isOpen}
      size='medium'
    >
      <div className='relative mb-4'>
        <IconButton
          onClick={handleClose}
          className='absolute top-0 right-0 text-primary-dark'
          aria-label={intl.get('CLOSE') || 'Close'}
          data-cy='close-modal-button'
          name='close'
        />
        <div className='flex items-center gap-2'>
          <Icon src={completedIcon} className='w-5 h-5' />
          <Typography className='text-lg font-normal leading-[28px]'>
            {intl.get('REQUESTS_LIST_PAGE.MODAL.COMPLETE_REQUEST.TITLE')}
          </Typography>
        </div>
      </div>

      {/* CGP-253: the Figma date field is 272px wide (not full modal width);
          the Datepicker is w-full so the input stretches within it. */}
      <FormItem
        label={intl.get(
          'REQUESTS_LIST_PAGE.MODAL.COMPLETE_REQUEST.COMPLETION_DATE'
        )}
        labelProps={{ required: true }}
        component='div'
        className='mb-4'
        style={{ width: '272px' }}
      >
        <Datepicker
          className='w-full'
          startDate={completionDate}
          onPickDate={handleDatePick}
          inputProps={{ 'aria-label': 'completion-date-field' }}
          startDateLabelProps={{ state: dateError ? 'error' : 'default' }}
          startDateError={
            dateError &&
            intl.get(
              'REQUESTS_LIST_PAGE.MODAL.COMPLETE_REQUEST.COMPLETION_DATE_ERROR'
            )
          }
        />
      </FormItem>

      <TextArea
        className='w-full'
        label={intl.get('REQUESTS_LIST_PAGE.MODAL.COMPLETE_REQUEST.COMMENTS')}
        textAreaProps={{
          placeholder: intl.get(
            'REQUESTS_LIST_PAGE.MODAL.COMPLETE_REQUEST.DETAILS'
          ),
          'data-cy': 'field_complete-request-comments',
          // CGP-253: Figma comments box is 124px tall (design-system default
          // is 176px); disable resize so it can't grow a scrollbar.
          style: { height: '124px', resize: 'none' },
        }}
        value={comments}
        onChange={(event) => setComments(event.target.value)}
      />
    </Modal>
  );
};

export default CompletedModal;
